import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AccountBalance,
  Refresh,
  Description,
  AccountBalanceWallet,
  Gavel,
  Analytics,
} from '@mui/icons-material';
import { adminCreditAPI, dashboardAPI, policiesAPI, escrowAPI, settlementAPI } from 'services/api';

// Sub-components
import KPISummaryCards from './components/KPISummaryCards';
import ExposureRadarPanel from './components/ExposureRadarPanel';
import CreditPipelinePanel from './components/CreditPipelinePanel';
import EscrowLedgerMonitorPanel from './components/EscrowLedgerMonitorPanel';
import RiskAlertsPanel from './components/RiskAlertsPanel';
import ApplicationsTab from './components/ApplicationsTab';
import EscrowTab from './components/EscrowTab';
import SettlementsTab from './components/SettlementsTab';
import RiskAnalyticsTab from './components/RiskAnalyticsTab';

/**
 * CreditControlCockpit
 *
 * Admin-only dashboard for comprehensive credit management:
 *   - 5 KPI summary cards at top
 *   - 4 analytical panels
 *   - 4 tabbed detail views (Applications, Escrow, Settlements, Risk Analytics)
 *
 * Uses existing backend APIs — no backend changes required.
 */
const CreditControlCockpit = () => {
  // ── State ──────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Data stores
  const [kpiData, setKpiData] = useState({});
  const [exposureData, setExposureData] = useState({});
  const [pipelineData, setPipelineData] = useState({});
  const [escrowPanelData, setEscrowPanelData] = useState({});
  const [riskData, setRiskData] = useState({});
  const [applications, setApplications] = useState([]);
  const [escrowEntries, setEscrowEntries] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [riskAnalyticsData, setRiskAnalyticsData] = useState({});

  // ── Data Loading ───────────────────────────────────────────
  const loadDashboardData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      // Fetch from available APIs in parallel
      const results = await Promise.allSettled([
        adminCreditAPI.getApplications(),  // 0 - enriched credit applications (admin)
        dashboardAPI.getAdminDashboard(),   // 1 - admin dashboard stats
        policiesAPI.getAll(),               // 2 - all policies for insurance data
        settlementAPI.getAll(),             // 3 - real settlement records
      ]);

      // ── Parse credit applications (enriched from admin endpoint) ──
      let creditApps = [];
      if (results[0].status === 'fulfilled') {
        creditApps = Array.isArray(results[0].value.data) ? results[0].value.data : [];
      }

      // ── Parse admin dashboard ──
      let adminStats = {};
      if (results[1].status === 'fulfilled') {
        adminStats = results[1].value.data || {};
      }

      // ── Parse policies ──
      if (results[2].status === 'fulfilled') {
        // policies available for future insurance-linked credit analysis
      }

      // ── Parse real settlement records ──
      let realSettlements = [];
      if (results[3].status === 'fulfilled') {
        realSettlements = Array.isArray(results[3].value.data) ? results[3].value.data : [];
      }

      // ── Derive KPI data ──
      const applied = creditApps.filter((a) => ['applied', 'pending'].includes((a.status || '').toLowerCase()));
      const approved = creditApps.filter((a) => (a.status || '').toLowerCase() === 'approved');
      const disbursed = creditApps.filter((a) => (a.status || '').toLowerCase() === 'disbursed');
      const active = creditApps.filter((a) => (a.status || '').toLowerCase() === 'active');
      const settled = creditApps.filter((a) => (a.status || '').toLowerCase() === 'settled');
      const rejected = creditApps.filter((a) => (a.status || '').toLowerCase() === 'rejected');
      const defaulted = creditApps.filter((a) => (a.status || '').toLowerCase() === 'defaulted');

      const totalSanctioned = creditApps.reduce((s, a) => s + (a.sanctioned_amount || a.approved_amount || 0), 0);
      const totalDisbursed = [...disbursed, ...active, ...settled].reduce(
        (s, a) => s + (a.disbursed_amount || a.sanctioned_amount || 0), 0
      );
      const outstanding = active.reduce(
        (s, a) => s + (a.outstanding_amount || a.disbursed_amount || a.sanctioned_amount || 0), 0
      );

      // Calculate NPA risk based on overdue/defaulted vs total active
      const activeAndDisbursed = disbursed.length + active.length;
      const npaRisk = activeAndDisbursed > 0
        ? ((defaulted.length / (activeAndDisbursed + defaulted.length)) * 100)
        : 0;

      setKpiData({
        totalCreditExposure: totalSanctioned || totalDisbursed || adminStats.total_premium || 0,
        totalApplications: creditApps.length,
        activeCreditCycles: active.length + disbursed.length,
        completedCycles: settled.length,
        disbursementThisMonth: totalDisbursed,
        disbursementCount: disbursed.length + active.length + settled.length,
        outstandingPrincipal: outstanding,
        overdueCount: defaulted.length,
        settlementsPending: creditApps.filter((a) => 
          ['active'].includes((a.status || '').toLowerCase()) && a.harvest_ready
        ).length || applied.length,
        settlementsPendingAmount: 0,
      });

      // ── Exposure data ──
      setExposureData({
        totalSanctioned,
        totalDisbursed,
        outstanding,
        npaRiskPercent: npaRisk,
      });

      // ── Pipeline data ──
      setPipelineData({
        applied: applied.length,
        approved: approved.length,
        disbursed: disbursed.length,
        active: active.length,
        settled: settled.length,
        rejected: rejected.length,
        defaulted: defaulted.length,
      });

      // ── Escrow panel data ──
      setEscrowPanelData({
        escrowBalance: totalDisbursed - outstanding,
        pendingVendorPayments: 0,
        settlementQueueCount: active.length,
        settlementQueueValue: outstanding,
        totalDeposits: totalDisbursed,
        totalWithdrawals: totalDisbursed - outstanding,
      });

      // ── Risk data ──
      setRiskData({
        highExposureDistricts: [],
        overdueHarvests: defaulted.length,
        vendorConcentrationRisk: [],
        defaultRiskCount: defaulted.length,
        npaRiskPercent: npaRisk,
      });

      // ── Tab data ──
      setApplications(creditApps.map((a) => ({
        ...a,
        farmer_name: a.farmer_name || a.user_name || `Farmer #${a.user_id || a.farmer_id || '—'}`,
        farmer_phone: a.farmer_phone || '',
        district: a.district || a.location || '',
        requested_amount: a.requested_amount || a.loan_amount || a.sanctioned_amount || 0,
      })));

      // Use real settlements from API if available, fallback to derived
      if (realSettlements.length > 0) {
        setSettlements(
          realSettlements.map((s) => ({
            id: s.id,
            credit_case_id: s.harvest_id,
            farmer_name: `User #${s.user_id || '—'}`,
            status: (s.status || 'PENDING').toUpperCase(),
            balance_before: parseFloat(s.gross_amount) || 0,
            total_deductions: parseFloat(s.deductions) || 0,
            farmer_payout: parseFloat(s.net_amount) || 0,
            created_at: s.settlement_date || s.created_at,
            // Full settlement fields for detail view
            settlement_number: s.settlement_number,
            gross_amount: s.gross_amount,
            credit_repayment: s.credit_repayment,
            insurance_premium: s.insurance_premium,
            marketplace_commission: s.marketplace_commission,
            net_amount: s.net_amount,
          }))
        );
      } else {
        setSettlements(
          settled.map((a) => ({
            credit_case_id: a.id,
            farmer_name: a.farmer_name || a.user_name || `Farmer #${a.user_id || '—'}`,
            status: 'COMPLETED',
            balance_before: a.disbursed_amount || 0,
            total_deductions: (a.disbursed_amount || 0) - (a.farmer_payout || 0),
            farmer_payout: a.farmer_payout || 0,
            created_at: a.settled_at || a.updated_at || a.created_at,
          }))
        );
      }

      // ── Fetch real escrow ledger entries across all credit cases ──
      const relevantApps = creditApps.filter((a) => {
        const s = (a.status || '').toLowerCase();
        return ['disbursed', 'active', 'settled', 'approved', 'defaulted'].includes(s);
      });
      if (relevantApps.length > 0) {
        try {
          const entryPromises = relevantApps.map((a) =>
            escrowAPI.getLedgerEntries(a.id).then(
              (res) => (res.data || []).map((e) => ({ ...e, credit_case_id: e.account_id || a.id })),
              () => []
            )
          );
          const allEntryResults = await Promise.all(entryPromises);
          const allEntries = allEntryResults.flat().sort(
            (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
          );
          setEscrowEntries(allEntries);
        } catch {
          setEscrowEntries([]);
        }
      }

      // ── Risk analytics ──
      setRiskAnalyticsData({
        npaRiskPercent: npaRisk,
        portfolioHealth: npaRisk > 10 ? 'At Risk' : npaRisk > 5 ? 'Moderate' : 'Good',
        avgLoanSize: creditApps.length > 0 ? totalSanctioned / creditApps.length : 0,
        totalLoans: creditApps.length,
        collectionRate: settled.length > 0
          ? ((settled.length / (settled.length + defaulted.length)) * 100)
          : 100,
        avgCycleDays: 120,
        defaultRiskCount: defaulted.length,
        overdueApplications: defaulted.length,
        staleEscrowCount: 0,
        missingHarvestCount: 0,
        incompleteSettlementCount: active.length,
      });

    } catch (err) {
      console.error('Credit cockpit data load error:', err);
      setError('Failed to load credit dashboard data. Some data may be unavailable.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // ── Approve / Reject handlers ─────────────────────────────
  const handleApprove = useCallback(async (applicationId, payload) => {
    try {
      await adminCreditAPI.approve(applicationId, payload);
      // Refresh data after successful approval
      await loadDashboardData(true);
      return { success: true };
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to approve application';
      return { success: false, error: msg };
    }
  }, [loadDashboardData]);

  const handleReject = useCallback(async (applicationId, reason) => {
    try {
      await adminCreditAPI.reject(applicationId, { reason });
      // Refresh data after successful rejection
      await loadDashboardData(true);
      return { success: true };
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to reject application';
      return { success: false, error: msg };
    }
  }, [loadDashboardData]);

  // ── Render ─────────────────────────────────────────────────
  const tabDefs = [
    { label: 'Applications', icon: <Description sx={{ fontSize: 18 }} /> },
    { label: 'Escrow', icon: <AccountBalanceWallet sx={{ fontSize: 18 }} /> },
    { label: 'Settlements', icon: <Gavel sx={{ fontSize: 18 }} /> },
    { label: 'Risk Analytics', icon: <Analytics sx={{ fontSize: 18 }} /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <AccountBalance sx={{ fontSize: 22, color: '#6366f1' }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Credit Control Cockpit</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Real-time overview of credit exposure, escrow, and risk analytics
          </p>
        </div>
        <div className="flex items-center gap-2 mt-3 sm:mt-0">
          <Tooltip title="Refresh data">
            <IconButton
              onClick={() => loadDashboardData(true)}
              disabled={refreshing}
              sx={{
                backgroundColor: '#f1f5f9',
                '&:hover': { backgroundColor: '#e2e8f0' },
              }}
            >
              <Refresh
                sx={{
                  fontSize: 20,
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
                }}
              />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 3 }}
          action={
            <Button size="small" onClick={() => loadDashboardData(true)}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* ── KPI Summary Cards ── */}
      <section className="mb-6">
        <KPISummaryCards data={kpiData} loading={loading} />
      </section>

      {/* ── 4 Panels (2x2 grid) ── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <ExposureRadarPanel data={exposureData} loading={loading} />
        <CreditPipelinePanel data={pipelineData} loading={loading} />
        <EscrowLedgerMonitorPanel data={escrowPanelData} loading={loading} />
        <RiskAlertsPanel data={riskData} loading={loading} />
      </section>

      {/* ── Tab Navigation ── */}
      <section className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="border-b border-gray-100 px-4">
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 56,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                color: '#6b7280',
                '&.Mui-selected': { color: '#6366f1' },
              },
              '& .MuiTabs-indicator': { backgroundColor: '#6366f1', height: 3, borderRadius: '3px 3px 0 0' },
            }}
          >
            {tabDefs.map((t, i) => (
              <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />
            ))}
          </Tabs>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <CircularProgress size={32} sx={{ color: '#6366f1' }} />
              <span className="ml-3 text-sm text-gray-500">Loading credit data...</span>
            </div>
          ) : (
            <>
              {activeTab === 0 && (
                <ApplicationsTab
                  applications={applications}
                  loading={false}
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              )}
              {activeTab === 1 && <EscrowTab entries={escrowEntries} loading={false} />}
              {activeTab === 2 && <SettlementsTab settlements={settlements} loading={false} />}
              {activeTab === 3 && <RiskAnalyticsTab data={riskAnalyticsData} loading={false} />}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default CreditControlCockpit;
